import { useEffect, useState } from 'react';
import type { BookFilterState } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  filters: BookFilterState;
  publishers: { id: string; name: string }[];
  onChange: (next: BookFilterState) => void;
}

export function BookListFilters({ filters, publishers, onChange }: Props) {
  const { t } = useTranslation();
  const [q, setQ] = useState(filters.q ?? '');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (q !== (filters.q ?? '')) {
        onChange({ ...filters, q: q || undefined, page: 1 });
      }
    }, 300);
    return () => window.clearTimeout(timer);
    // Intentionally depend only on q so filter-change clears don't re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => { setQ(filters.q ?? ''); }, [filters.q]);

  const parseNum = (v: string): number | undefined =>
    v === '' ? undefined : Number(v);

  return (
    <div className="flex flex-wrap gap-3 items-end">
      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{t('App.Admin.Books.BookListFilters.SearchLabel')}</span>
        <input
          type="text" value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t('App.Admin.Books.BookListFilters.SearchPlaceholder')}
          className="border rounded px-3 py-2 w-72 min-h-[44px]"
          aria-label={t('App.Admin.Books.BookListFilters.SearchAriaLabel')}
        />
      </label>

      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{t('App.Admin.Books.BookListFilters.StatusLabel')}</span>
        <select
          value={filters.status ?? ''}
          onChange={e => onChange({
            ...filters,
            status: parseNum(e.target.value) as BookFilterState['status'],
            page: 1,
          })}
          className="border rounded px-3 py-2 min-h-[44px]"
        >
          <option value="">{t('App.Admin.Books.BookListFilters.StatusAll')}</option>
          <option value="1">{t('App.Common.BookStatus.Draft')}</option>
          <option value="2">{t('App.Common.BookStatus.Published')}</option>
          <option value="3">{t('App.Common.BookStatus.Archived')}</option>
        </select>
      </label>

      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{t('App.Admin.Books.BookListFilters.FormatLabel')}</span>
        <select
          value={filters.format ?? ''}
          onChange={e => onChange({
            ...filters,
            format: parseNum(e.target.value) as BookFilterState['format'],
            page: 1,
          })}
          className="border rounded px-3 py-2 min-h-[44px]"
        >
          <option value="">{t('App.Admin.Books.BookListFilters.FormatAll')}</option>
          <option value="1">{t('App.Common.Format.EPUB')}</option>
          <option value="2">{t('App.Common.Format.PDF')}</option>
        </select>
      </label>

      <label className="flex flex-col text-sm">
        <span className="text-neutral-700">{t('App.Admin.Books.BookListFilters.PublisherLabel')}</span>
        <select
          value={filters.publisherId ?? ''}
          onChange={e => onChange({
            ...filters,
            publisherId: e.target.value || undefined,
            page: 1,
          })}
          className="border rounded px-3 py-2 min-h-[44px]"
        >
          <option value="">{t('App.Admin.Books.BookListFilters.PublisherAll')}</option>
          {publishers.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-2 text-sm mb-2">
        <input
          type="checkbox"
          checked={filters.drm === true}
          onChange={e => onChange({
            ...filters,
            drm: e.target.checked ? true : undefined,
            page: 1,
          })}
        />
        {t('App.Admin.Books.BookListFilters.DrmEnabled')}
      </label>
    </div>
  );
}
